import { MessageSquare, Clock, CheckCircle, AlertCircle, Search, Filter } from 'lucide-react';

const tickets = [
  {
    id: 'SUP-001',
    subject: 'Campaign not sending emails',
    customer: 'Acme Corp',
    status: 'open',
    priority: 'high',
    createdAt: '2026-03-15T10:30:00Z',
    lastUpdated: '2026-03-15T14:20:00Z',
  },
  {
    id: 'SUP-002',
    subject: 'API rate limit error',
    customer: 'TechStart Inc',
    status: 'in_progress',
    priority: 'medium',
    createdAt: '2026-03-14T09:15:00Z',
    lastUpdated: '2026-03-15T11:45:00Z',
  },
  {
    id: 'SUP-003',
    subject: 'Billing inquiry',
    customer: 'Growth Labs',
    status: 'resolved',
    priority: 'low',
    createdAt: '2026-03-13T16:00:00Z',
    lastUpdated: '2026-03-14T10:30:00Z',
  },
];

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-2xl font-bold">Stratixa Support</h1>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  className="pl-10 pr-4 py-2 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-secondary">
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-4 gap-4">
            <StatCard label="Open Tickets" value="12" icon={MessageSquare} />
            <StatCard label="In Progress" value="8" icon={Clock} />
            <StatCard label="Resolved Today" value="15" icon={CheckCircle} />
            <StatCard label="High Priority" value="3" icon={AlertCircle} />
          </div>
        </div>
      </section>

      {/* Tickets Table */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-secondary/20">
              <tr>
                <th className="text-left p-4 font-semibold">Ticket ID</th>
                <th className="text-left p-4 font-semibold">Subject</th>
                <th className="text-left p-4 font-semibold">Customer</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Priority</th>
                <th className="text-left p-4 font-semibold">Created</th>
                <th className="text-left p-4 font-semibold">Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr key={ticket.id} className="border-b border-border hover:bg-secondary/10 cursor-pointer">
                  <td className="p-4 font-medium">{ticket.id}</td>
                  <td className="p-4">{ticket.subject}</td>
                  <td className="p-4">{ticket.customer}</td>
                  <td className="p-4">
                    <StatusBadge status={ticket.status} />
                  </td>
                  <td className="p-4">
                    <PriorityBadge priority={ticket.priority} />
                  </td>
                  <td className="p-4 text-muted-foreground">{formatDate(ticket.createdAt)}</td>
                  <td className="p-4 text-muted-foreground">{formatDate(ticket.lastUpdated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }: { label: string; value: string; icon: any }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-primary" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || ''}`}>
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const styles: Record<string, string> = {
    high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    medium: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    low: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[priority] || ''}`}>
      {priority.toUpperCase()}
    </span>
  );
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
